/**
 * Pure rules of the idea composer — kept out of React so they can be unit
 * tested (ideaCore.test.ts) and read in one place.
 */
import { CATALOG_THEME_LABEL_KEYS, INTEREST_KEYS, PRIMARY_INTEREST_KEYS, isInterestKey } from '../../lib/interests';
import type { IdeaBatch, Selection } from './types';

/** Chips shown in the row; the rest go to "More". */
export const ROW_COUNT = 6;
/** How many pinned (recent / seeded) keys may take a slot in the row. */
export const PINNED_IN_ROW = 2;

export function selectionId(sel: Selection): string {
  if (!sel) return 'generic';
  return sel.kind === 'key' ? `key:${sel.key}` : `custom:${sel.label.trim().toLowerCase()}`;
}

/** The chip row: up to two pinned keys (recent themes, an ad's seed), then the most-searched ones; no duplicates, unknown keys dropped. */
export function rowKeys(pinned: readonly string[]): { rowKeys: string[]; moreKeys: string[] } {
  const pins: string[] = [];
  for (const k of pinned) {
    if (isInterestKey(k) && !pins.includes(k)) pins.push(k);
    if (pins.length >= PINNED_IN_ROW) break;
  }
  const row = [...pins, ...PRIMARY_INTEREST_KEYS.filter((k) => !pins.includes(k))].slice(0, ROW_COUNT);
  return { rowKeys: row, moreKeys: INTEREST_KEYS.filter((k) => !row.includes(k)) };
}

/**
 * stories.theme_tag truth: the current text is one of the fetched ideas for a
 * chip. Typing, editing, softening, a custom topic or the fallback classics
 * clear it.
 */
export function resolveThemeTag(batch: IdeaBatch | null, value: string): string | null {
  return batch && !batch.fallback && batch.keyTag && batch.ideas.includes(value) ? batch.keyTag : null;
}

/** The slide's wand: the next fetched idea not shown yet, or null when the batch is used up. */
export function nextWandIdea(ideas: readonly string[], used: readonly string[]): string | null {
  return ideas.find((idea) => idea.trim() && !used.includes(idea)) ?? null;
}

/**
 * The reader's closing slide: what the ideas should be about, derived from
 * the story just read. Interest key → that chip; seeded catalog tag → its
 * localized label as a typed topic; anything else → the title as context.
 */
export function selectionForStoryTheme(
  theme: string | null | undefined,
  title: string,
  t: (key: string) => string,
): { selection: Selection; context: string } {
  const tag = (theme || '').trim().toLowerCase();
  if (isInterestKey(tag)) return { selection: { kind: 'key', key: tag }, context: '' };
  const labelKey = CATALOG_THEME_LABEL_KEYS[tag];
  if (labelKey) return { selection: { kind: 'custom', label: t(labelKey) }, context: '' };
  return { selection: null, context: title };
}
